import { pick } from './utilities/pick';

export const BRAND_NAME = {
  Freddie: 'freddie',
  FreddieUSA: 'freddie usa',
  Frida: 'frida',
  PurposeMed: 'purposemed',
} as const;

export const BRAND_DISPLAY_NAME = {
  [BRAND_NAME.Freddie]: 'Freddie',
  [BRAND_NAME.FreddieUSA]: 'Freddie',
  [BRAND_NAME.Frida]: 'Frida',
  [BRAND_NAME.PurposeMed]: 'PurposeMed',
};

export type Brand = (typeof BRAND_NAME)[keyof typeof BRAND_NAME];

export enum BrandId {
  Freddie = '1',
  Frida = '2',
  FreddieUSA = '3',
}

const BRAND_ID_BY_VALUE: Partial<Record<string, BrandId>> = {
  [BrandId.Freddie]: BrandId.Freddie,
  [BrandId.Frida]: BrandId.Frida,
  [BrandId.FreddieUSA]: BrandId.FreddieUSA,
};

export const toBrandId = (id?: string | null): BrandId | undefined =>
  id ? BRAND_ID_BY_VALUE[id] : undefined;

export const BRAND_ID_DISPLAY_NAME: Record<BrandId, string> = {
  [BrandId.Freddie]: BRAND_DISPLAY_NAME[BRAND_NAME.Freddie],
  [BrandId.Frida]: BRAND_DISPLAY_NAME[BRAND_NAME.Frida],
  [BrandId.FreddieUSA]: BRAND_DISPLAY_NAME[BRAND_NAME.FreddieUSA],
};

export enum KeyboardKeys {
  Enter = 'Enter',
}

export type FormattingContext = 'patient' | 'provider';

/**
 * ISO 8601 date string
 * @example '2020-01-01'
 */
export type ISO8601Date = string;

/**
 * ISO 8601 datetime string
 * @example '2020-01-01T00:00:00.000Z'
 */
export type ISO8601DateTime = string;

export type SupportedLanguage = 'en' | 'es' | 'fr' | (string & {});
export const QuestionnaireDefinitionName = {
  AddOnRefill: 'add_on_refill_questionnaire',
  Adherence: 'adherence_questionnaire',
  AsyncPrep: 'async_prep_questionnaire',
  DoxyPep: 'doxy_pep_questionnaire',
  PausePrepTreatmentReason: 'pause_prep_treatment_reason_questionnaire',
  PrepIntake: 'intake_questionnaire',
  StopPrepTreatmentReason: 'stop_prep_treatment_reason_questionnaire',
} as const;

export type QuestionnaireDefinition =
  (typeof QuestionnaireDefinitionName)[keyof typeof QuestionnaireDefinitionName];

export type NonNullableVariantProps<T> = {
  [K in keyof T]: Exclude<T[K], null>;
};

export type StripWhiteSpace<T> = T extends `${infer U} ${infer V}`
  ? `${U}${StripWhiteSpace<V>}`
  : T;

export const ROLE = {
  Patient: 'PATIENT',
  Clinician: 'CLINICIAN',
  CX: 'CX',
  MOA: 'MOA',
  SuperUser: 'ADMIN',
  Pharmacy: 'PHARMACY',
} as const;

export type Role = (typeof ROLE)[keyof typeof ROLE];

export const ProviderRoles = pick(ROLE, 'Clinician', 'CX', 'MOA', 'SuperUser');

export const PROVIDER_ROLES = [
  ROLE.Clinician,
  ROLE.CX,
  ROLE.MOA,
  ROLE.SuperUser,
  ROLE.Pharmacy,
];
